import { Dropdown, Menu, Tooltip } from 'antd'
import cx from 'classnames'
import * as React from 'react'

import Avatar from 'common/Avatar/Avatar'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { getBackground50Class, getBorderClass } from 'common/PresenceCursor/PresenceCursor'
import store from 'redux/store'
import { DocPresence } from 'stores/DocPresence'
import { PresenceUser, PresentUser, UsersMetadata } from 'types'
import { getInitials } from 'utils'
import { checkFeatureFlagUserGroup, USERS } from 'utils/featureFlagUserGroups'
import { getUserProfilePath } from 'utils/urlUtils'
import { consolidatedUserId } from './consolidatedUserId'
import Styles from './PresenceAvatars.module.scss'

interface Props {
  users?: PresenceUser[]
  showMinCount: number
  showMaxCount: number
  className?: string
  metadata?: UsersMetadata
  docPresence?: DocPresence | null
}

const NOT_LOGGED_IN_USER_NAME = 'Anonymous User'
const BLAZE_AVATAR_SIZE_PX = 28

export const userName = (user: PresenceUser) => {
  const fullName = user && user.first_name && user.last_name && `${user.first_name} ${user.last_name}`
  return fullName || NOT_LOGGED_IN_USER_NAME
}

export const getPresenceUserPath = (user: PresenceUser) => {
  const userId = user && (user as PresentUser).id
  const displayName = userName(user)
  return getUserProfilePath({ id: userId, displayName })
}

export const PresenceAvatar = ({
  user,
  color,
  seed,
  onClick,
}: {
  user: PresenceUser
  color: string | undefined
  seed: number
  onClick?: (e: React.MouseEvent) => void
}) => {
  const size = BLAZE_AVATAR_SIZE_PX
  if (user?.first_name && user?.last_name) {
    return (
      <Avatar
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        className={cx({ [getBackground50Class(color!)]: color })}
        label={getInitials(userName(user))}
        url={(user as PresentUser).avatar_url}
        size={size}
        onClick={onClick}
      />
    )
  }

  return <Avatar size={size} seed={seed} />
}

export const PresenceAvatars = ({
  users = [],
  showMinCount,
  showMaxCount,
  className,
  metadata = {},
  docPresence,
}: Props) => {
  const size = BLAZE_AVATAR_SIZE_PX
  // Use manual flag because this component is used outside of providers
  const canObserveUsers = checkFeatureFlagUserGroup([USERS.withAlmanacEmail], store.getState().auth?.email)
  if (users.length < showMinCount) return null

  // "null" last (aka not logged in users), with present first (aka currently present in doc).
  const sortedUsers = users.sort((firstUser, secondUser) => {
    // nulls last
    if (!firstUser && secondUser) return 1
    if (!secondUser) return 0

    // Those having present before those having not. in both groups, order by ID.
    const firstUserId = consolidatedUserId(firstUser)
    const secondUserId = consolidatedUserId(secondUser)
    const firstUserInDoc = !!metadata[consolidatedUserId(firstUser)]?.present
    const secondUserInDoc = !!metadata[consolidatedUserId(secondUser)]?.present

    if (firstUserInDoc && !secondUserInDoc) return -1
    if (!firstUserInDoc && secondUserInDoc) return 1

    return firstUserId < secondUserId ? -1 : 1
  })

  const showUsers = sortedUsers.slice(0, showMaxCount)
  const hiddenUsers = sortedUsers.slice(showMaxCount, sortedUsers.length)
  const dropdownOverlay = (
    <Menu>
      {hiddenUsers.map((user, i) => {
        const userId = user ? consolidatedUserId(user) : i
        const userPresent = metadata[userId]?.present
        const color = metadata[userId]?.color
        return (
          <Menu.Item key={userId} className={Styles.dropdownItem}>
            <ConditionalWrapper
              condition={!canObserveUsers}
              wrapper={(children) => (
                <a
                  href={getPresenceUserPath(user)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={metadata[userId]?.onClick}
                >
                  {children}
                </a>
              )}
            >
              <div className={Styles.dropdownItem}>
                <div
                  onClick={metadata[userId]?.onClick}
                  className={cx(Styles.avatarContainer, {
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    [getBorderClass(color!)]: userPresent && color,
                  })}
                >
                  <PresenceAvatar user={user} seed={userId} color={userPresent ? color : undefined} />
                </div>
                <div className={Styles.dropdownName}>{userName(user)}</div>
              </div>
            </ConditionalWrapper>
          </Menu.Item>
        )
      })}
    </Menu>
  )

  return (
    <div
      className={`${Styles.container} ${className || ''}`}
      style={{ maxWidth: `${size * (showMaxCount + 1) + 12}px` }} // 12px to account for left and right paddings
    >
      {showUsers.map((user, i) => {
        const userId = user ? consolidatedUserId(user) : i
        const userPresent = metadata[userId]?.present
        const color = metadata[userId]?.color
        return (
          <Tooltip key={userId} placement="bottom" title={userName(user)}>
            <ConditionalWrapper
              condition={!canObserveUsers}
              wrapper={(children) => (
                <a
                  href={getPresenceUserPath(user)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={metadata[userId]?.onClick}
                >
                  {children}
                </a>
              )}
            >
              <div
                className={cx(Styles.avatarContainer, {
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  [getBorderClass(color!)]: userPresent && color,
                })}
              >
                <PresenceAvatar
                  user={user}
                  color={userPresent ? color : undefined}
                  seed={userId}
                  onClick={(e) => {
                    if (!canObserveUsers) return

                    if (docPresence) {
                      docPresence.followedUser.user = user
                      docPresence.followedUser.meta = metadata[userId]
                    }
                    metadata[userId]?.onClick?.(e)
                  }}
                />
              </div>
            </ConditionalWrapper>
          </Tooltip>
        )
      })}
      {hiddenUsers.length > 0 && (
        <Dropdown overlay={dropdownOverlay} placement="bottomCenter">
          <div className={Styles.avatarContainer}>
            <Avatar label={`+${hiddenUsers.length}`} size={size} backgroundColor="var(--dark-8)" />
          </div>
        </Dropdown>
      )}
    </div>
  )
}
